import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  Section,
  WorkflowCopyOptions,
  WorkflowCopyConfiguration,
  WorkflowConfigurationSaveAction,
  Workflow
} from 'src/app/models';
import { Tag } from 'src/app/models/tag';
import {
  PrepareContractorTypeImportResponse,
  FinalizeContractorTypeImportRequest,
  ContractorTypeTagMapping
} from 'src/app/models/contractor-type-import';
import { ContractorService } from 'src/app/services/contractor.service';
import { WorkflowService } from 'src/app/services/workflow.service';
import { WorkflowContextService } from 'src/app/services';
import { DataService } from 'src/app/services/data.service';

export interface ImportContractorTypeModalData {
  targetClientId: string;
}

@Component({
  selector: 'wm-import-contractor-type-modal',
  templateUrl: './import-contractor-type-modal.component.html',
  styleUrls: ['./import-contractor-type-modal.component.scss'],
  standalone: false
})
export class ImportContractorTypeModalComponent implements OnInit {
  WorkflowConfigurationSaveAction = WorkflowConfigurationSaveAction;

  loading = false;
  isPreparing = false;
  isSaving = false;
  fileSelectError: string = null;
  prepareResponse: PrepareContractorTypeImportResponse = null;

  // Fee editor forms
  registerFeeUpdatesForm: FormGroup = this.fb.group({});
  renewFeeUpdatesForm: FormGroup = this.fb.group({});

  // Email editor forms
  registerEmailUpdatesForm: FormGroup = this.fb.group({});
  renewEmailUpdatesForm: FormGroup = this.fb.group({});
  expandedRegisterEmailIndex = 0;
  expandedRenewEmailIndex = 0;

  // Print template editor forms
  registerPrintTemplateUpdatesForm: FormGroup = this.fb.group({});
  renewPrintTemplateUpdatesForm: FormGroup = this.fb.group({});
  registerPrintTemplateFormReady = false;
  renewPrintTemplateFormReady = false;

  // Loaded workflow objects for print template / fee / email editors
  registerWorkflowObj: Workflow = null;
  renewWorkflowObj: Workflow = null;

  registerWorkflowOptions: WorkflowCopyOptions = null;
  renewWorkflowOptions: WorkflowCopyOptions = null;

  registerWorkflowConfig: WorkflowCopyConfiguration = null;
  renewWorkflowConfig: WorkflowCopyConfiguration = null;

  availableTags: Tag[] = [];

  // Tag mapping state: sourceName -> targetTagId (null = create new)
  tagMappings: { sourceName: string; targetTagId: string | null }[] = [];

  sections: Section[] = [
    {
      key: 'file-select',
      label: 'Select Import File',
      enabled: true,
      completed: false
    },
    {
      key: 'ct-settings',
      label: 'Contractor Type Settings',
      enabled: true,
      completed: false
    },
    {
      key: 'register-settings',
      label: 'Register: Workflow Settings',
      enabled: false,
      completed: false
    },
    {
      key: 'register-roles',
      label: 'Register: Configure Workflow Roles',
      enabled: false,
      completed: false
    },
    {
      key: 'register-custom-lists',
      label: 'Register: Configure Workflow Custom Lists & Items',
      enabled: false,
      completed: false
    },
    {
      key: 'register-fee-recipients',
      label: 'Register: Configure Fee Recipients',
      enabled: false,
      completed: false
    },
    {
      key: 'register-email-footers',
      label: 'Register: Configure Workflow Email Footers',
      enabled: false,
      completed: false
    },
    {
      key: 'register-fees',
      label: 'Register: Review & Edit Workflow Fees',
      enabled: false,
      completed: false
    },
    {
      key: 'register-emails',
      label: 'Register: Review & Edit Workflow Emails',
      enabled: false,
      completed: false
    },
    {
      key: 'register-print-templates',
      label: 'Register: Review & Edit Workflow Print Templates',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-settings',
      label: 'Renew: Workflow Settings',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-roles',
      label: 'Renew: Configure Workflow Roles',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-custom-lists',
      label: 'Renew: Configure Workflow Custom Lists & Items',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-fee-recipients',
      label: 'Renew: Configure Fee Recipients',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-email-footers',
      label: 'Renew: Configure Workflow Email Footers',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-fees',
      label: 'Renew: Review & Edit Workflow Fees',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-emails',
      label: 'Renew: Review & Edit Workflow Emails',
      enabled: false,
      completed: false
    },
    {
      key: 'renew-print-templates',
      label: 'Renew: Review & Edit Workflow Print Templates',
      enabled: false,
      completed: false
    },
    {
      key: 'tags',
      label: 'Map Contractor Type Tags',
      enabled: false,
      completed: false
    },
    {
      key: 'review',
      label: 'Review & Import',
      enabled: false,
      completed: false
    }
  ];

  expandedIndex = 0;

  ctSettingsForm: FormGroup;

  get allSectionsCompleted(): boolean {
    return this.sections.filter(s => s.enabled).every(s => s.completed);
  }

  constructor(
    private dialogRef: MatDialogRef<ImportContractorTypeModalComponent>,
    private fb: FormBuilder,
    private _contractorSvc: ContractorService,
    private _workflowSvc: WorkflowService,
    private _dataSvc: DataService,
    private _context: WorkflowContextService,
    private _toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ImportContractorTypeModalData
  ) {}

  ngOnInit(): void {
    this.ctSettingsForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    this.fileSelectError = null;
    const reader = new FileReader();
    reader.onload = () => {
      const packageJson = reader.result as string;
      this.isPreparing = true;

      this._contractorSvc
        .prepareContractorTypeImport({
          packageJson,
          targetClientId: this.data.targetClientId
        })
        .subscribe({
          next: (response: PrepareContractorTypeImportResponse) => {
            this.prepareResponse = response;

            this.ctSettingsForm.patchValue({
              name: response.contractorTypeName || '',
              description: response.contractorTypeDescription || ''
            });

            const sourceTags = response.tags || (response as any).Tags || [];

            this.tagMappings = sourceTags.map(t => ({
              sourceName: t,
              targetTagId: null
            }));

            forkJoin({
              register: this._workflowSvc.getCopyWorkflowConfigurationOptions(
                response.registerWorkflowId,
                this.data.targetClientId
              ),
              renew: this._workflowSvc.getCopyWorkflowConfigurationOptions(
                response.renewWorkflowId,
                this.data.targetClientId
              ),
              tags: this._dataSvc.getTagsForContractorTypes(
                this.data.targetClientId
              ),
              registerWorkflow: this._workflowSvc.getWorkflow(
                response.registerWorkflowId,
                true,
                true
              ),
              renewWorkflow: this._workflowSvc.getWorkflow(
                response.renewWorkflowId,
                true,
                true
              )
            }).subscribe({
              next: ({
                register,
                renew,
                tags,
                registerWorkflow,
                renewWorkflow
              }) => {
                this.registerWorkflowOptions = register;
                this.renewWorkflowOptions = renew;
                this.availableTags = tags || [];
                this.registerWorkflowObj = registerWorkflow;
                this.renewWorkflowObj = renewWorkflow;

                this.registerWorkflowConfig =
                  register?.sourceWorkflowElements ??
                  new WorkflowCopyConfiguration();
                this.registerWorkflowConfig.action =
                  WorkflowConfigurationSaveAction.Import;

                this.renewWorkflowConfig =
                  renew?.sourceWorkflowElements ??
                  new WorkflowCopyConfiguration();
                this.renewWorkflowConfig.action =
                  WorkflowConfigurationSaveAction.Import;

                this.enableSections();
                this.isPreparing = false;

                // Build editor forms for fees / emails / print templates
                this.buildEditorForms(this.registerWorkflowConfig, 'register');
                this.buildEditorForms(this.renewWorkflowConfig, 'renew');

                // Advance past the file-select step
                const fileSelectIdx = this.sections.findIndex(
                  s => s.key === 'file-select'
                );
                if (fileSelectIdx >= 0) {
                  this.sections[fileSelectIdx].completed = true;
                }
                this.nextSection();
              },
              error: () => {
                this.isPreparing = false;
                this.fileSelectError =
                  'Failed to load workflow import options. Please try again.';
              }
            });
          },
          error: () => {
            this.isPreparing = false;
            this.fileSelectError =
              'Invalid export file. Please select a valid contractor type export JSON.';
            // reset the file input
            input.value = '';
          }
        });
    };
    reader.onerror = () => {
      this.fileSelectError = 'Could not read the file. Please try again.';
    };
    reader.readAsText(file);
  }

  private enableSections() {
    const hasRegisterRoles =
      (this.registerWorkflowOptions?.sourceWorkflowElements?.roleMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRegisterLists =
      (this.registerWorkflowOptions?.sourceWorkflowElements?.customListMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRegisterFeeRecipients =
      (this.registerWorkflowOptions?.sourceWorkflowElements
        ?.feeRecipientMappings?.mappings?.length ?? 0) > 0;
    const hasRegisterEmailFooters =
      (this.registerWorkflowOptions?.sourceWorkflowElements?.emailFooterMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRegisterFees =
      (this.registerWorkflowOptions?.sourceWorkflowElements?.feeUpdates
        ?.feeDataEntities?.length ?? 0) > 0;
    const hasRegisterEmails =
      (this.registerWorkflowOptions?.sourceWorkflowElements?.emailUpdates
        ?.emailActivities?.length ?? 0) > 0;
    const hasRegisterPrintTemplates =
      (this.registerWorkflowOptions?.sourceWorkflowElements
        ?.printTemplateUpdates?.printTemplateActivities?.length ?? 0) > 0;

    const hasRenewRoles =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.roleMappings?.mappings
        ?.length ?? 0) > 0;
    const hasRenewLists =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.customListMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRenewFeeRecipients =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.feeRecipientMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRenewEmailFooters =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.emailFooterMappings
        ?.mappings?.length ?? 0) > 0;
    const hasRenewFees =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.feeUpdates
        ?.feeDataEntities?.length ?? 0) > 0;
    const hasRenewEmails =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.emailUpdates
        ?.emailActivities?.length ?? 0) > 0;
    const hasRenewPrintTemplates =
      (this.renewWorkflowOptions?.sourceWorkflowElements?.printTemplateUpdates
        ?.printTemplateActivities?.length ?? 0) > 0;

    const hasTags =
      ((this.prepareResponse?.tags || (this.prepareResponse as any)?.Tags || [])
        .length ?? 0) > 0;

    const sectionEnabled: { [key: string]: boolean } = {
      'file-select': true,
      'ct-settings': true,
      'register-settings': true,
      'register-roles': hasRegisterRoles,
      'register-custom-lists': hasRegisterLists,
      'register-fee-recipients': hasRegisterFeeRecipients,
      'register-email-footers': hasRegisterEmailFooters,
      'register-fees': hasRegisterFees,
      'register-emails': hasRegisterEmails,
      'register-print-templates': hasRegisterPrintTemplates,
      'renew-settings': true,
      'renew-roles': hasRenewRoles,
      'renew-custom-lists': hasRenewLists,
      'renew-fee-recipients': hasRenewFeeRecipients,
      'renew-email-footers': hasRenewEmailFooters,
      'renew-fees': hasRenewFees,
      'renew-emails': hasRenewEmails,
      'renew-print-templates': hasRenewPrintTemplates,
      tags: hasTags,
      review: true
    };

    this.sections.forEach(s => {
      s.enabled = sectionEnabled[s.key] ?? false;
      // Sections with no items are auto-completed
      if (
        s.enabled &&
        sectionEnabled[s.key] === true &&
        s.key !== 'ct-settings' &&
        s.key !== 'review'
      ) {
        // sections enabled by content (not forced) start as not completed
      }
    });

    // Auto-complete sections that have no content to configure
    this.sections.forEach(s => {
      if (!sectionEnabled[s.key]) {
        s.completed = true;
      }
    });
  }

  nextSection() {
    this.sections[this.expandedIndex].completed = true;
    const next = this.sections.findIndex(
      (s, i) => i > this.expandedIndex && s.enabled
    );
    if (next >= 0) {
      this.expandedIndex = next;
    }
  }

  close() {
    this.dialogRef.close(null);
  }

  private buildEditorForms(
    config: WorkflowCopyConfiguration,
    prefix: 'register' | 'renew'
  ): void {
    if (!config) return;

    // Fees
    const feeForm = this.fb.group({});
    if (config.feeUpdates?.feeDataEntities?.length) {
      const activities: any[] = [];
      config.feeUpdates.feeDataEntities.forEach((fde: any) => {
        const act = fde.parentActivitySurrogate;
        if (act && !activities.some((a: any) => a.id === act.id)) {
          activities.push(act);
        }
      });
      activities.forEach(act =>
        this.populateDataEntitiesBeforeMeCache(
          act,
          config.sourceWorkflowVersionId
        )
      );
      config.feeUpdates.feeDataEntities.forEach((fde: any) => {
        feeForm.addControl(fde.templateCode, this.fb.group({}));
      });
    }
    if (prefix === 'register') {
      this.registerFeeUpdatesForm = feeForm;
    } else {
      this.renewFeeUpdatesForm = feeForm;
    }

    // Emails
    const emailForm = this.fb.group({});
    if (config.emailUpdates?.emailActivities?.length) {
      config.emailUpdates.emailActivities.forEach((act: any) => {
        this.populateDataEntitiesBeforeMeCache(
          act,
          config.sourceWorkflowVersionId
        );
        emailForm.addControl(act.id, new UntypedFormGroup({}));
      });
    }
    if (prefix === 'register') {
      this.registerEmailUpdatesForm = emailForm;
    } else {
      this.renewEmailUpdatesForm = emailForm;
    }

    // Print templates
    const printForm = this.fb.group({});
    if (config.printTemplateUpdates?.printTemplateActivities?.length) {
      config.printTemplateUpdates.printTemplateActivities
        .filter((a: any) => a && a.id)
        .forEach((act: any) => {
          this.populateDataEntitiesBeforeMeCache(
            act,
            config.sourceWorkflowVersionId
          );
          printForm.addControl(act.id, this.fb.group({}));
        });
      setTimeout(() => {
        if (prefix === 'register') {
          this.registerPrintTemplateFormReady = true;
        } else {
          this.renewPrintTemplateFormReady = true;
        }
      });
    }
    if (prefix === 'register') {
      this.registerPrintTemplateUpdatesForm = printForm;
    } else {
      this.renewPrintTemplateUpdatesForm = printForm;
    }
  }

  private populateDataEntitiesBeforeMeCache(
    activity: any,
    workflowVersionId: string
  ): void {
    this._workflowSvc.clearDataEntityBeforeMeCache(activity.id);
    this._workflowSvc
      .getDataEntitiesBeforeMe(
        null,
        activity,
        null,
        [],
        true,
        workflowVersionId
      )
      .subscribe();
  }

  finishImport() {
    if (this.isSaving) return;
    this.isSaving = true;

    // Generate save IDs client-side so we can reference them in FinalizeContractorTypeImport
    // regardless of what saveWorkflowCopyConfiguration returns (boolean).
    const regSaveId = crypto.randomUUID();
    const renSaveId = crypto.randomUUID();

    const saveRegisterConfig$ = this._workflowSvc.saveWorkflowCopyConfiguration(
      this.registerWorkflowConfig,
      this.data.targetClientId,
      regSaveId
    );

    const saveRenewConfig$ = this._workflowSvc.saveWorkflowCopyConfiguration(
      this.renewWorkflowConfig,
      this.data.targetClientId,
      renSaveId
    );

    forkJoin({
      registerSave: saveRegisterConfig$,
      renewSave: saveRenewConfig$
    }).subscribe({
      next: () => {
        const request: FinalizeContractorTypeImportRequest = {
          pendingImportId: this.prepareResponse.pendingImportId,
          targetClientId: this.data.targetClientId,
          newName: this.ctSettingsForm.value.name,
          newDescription: this.ctSettingsForm.value.description,
          registerWorkflowName: this.registerWorkflowConfig?.workflowUpdates
            ?.name,
          renewWorkflowName: this.renewWorkflowConfig?.workflowUpdates?.name,
          registerWorkflowSaveId: regSaveId,
          renewWorkflowSaveId: renSaveId,
          tagMappings: this.tagMappings.map(
            m =>
              ({
                sourceName: m.sourceName,
                targetTagId: m.targetTagId
              } as ContractorTypeTagMapping)
          )
        };

        this._contractorSvc.finalizeContractorTypeImport(request).subscribe({
          next: ct => {
            this.isSaving = false;
            this._toastr.success(
              `"${ct?.name || request.newName}" imported successfully!`
            );
            this.dialogRef.close(ct);
          },
          error: err => {
            this.isSaving = false;
            this._toastr.error(
              'Import failed. Please try again or contact support.'
            );
            console.error('FinalizeContractorTypeImport error', err);
          }
        });
      },
      error: err => {
        this.isSaving = false;
        this._toastr.error('Failed to save workflow configurations.');
        console.error('SaveWorkflowCopyConfiguration error', err);
      }
    });
  }
}
